<template>
  <div class="app-container">
    <!-- Header with Search Filter and Project Summary -->
    <div class="navigate-bar">
      <div class="search-filter-section">
        <!-- Search Bar -->
        <el-input
          v-model="searchQuery"
          placeholder="Search by dataset ID, name, description, cancer type, tissue type, or PubMed ID..."
          prefix-icon="el-icon-search"
          size="medium"
          clearable
          class="search-input"
          @input="handleSearch">
        </el-input>
        
        <!-- Filter Options -->
        <el-select
          v-model="selectedCancerType"
          placeholder="Cancer Type"
          clearable
          size="medium"
          class="filter-select"
          @change="applyFilters">
          <el-option
            v-for="type in cancerTypes"
            :key="type"
            :label="type"
            :value="type">
          </el-option>
        </el-select>
        
        <el-select
          v-model="selectedTissueType"
          placeholder="Tissue Type"
          clearable
          size="medium"
          class="filter-select"
          @change="applyFilters">
          <el-option
            v-for="type in tissueTypes"
            :key="type"
            :label="type"
            :value="type">
          </el-option>
        </el-select>
        
        <el-select
          v-model="selectedDatasetType"
          placeholder="Dataset Type"
          clearable
          size="medium"
          class="filter-select"
          @change="applyFilters">
          <el-option label="Public" value="public">
            <span><i class="el-icon-view" style="color: #67C23A;"></i> Public</span>
          </el-option>
          <el-option v-if="isLoggedIn && currentUser.role !== 'community'" label="Private" value="private">
            <span><i class="el-icon-lock" style="color: #E6A23C;"></i> Private</span>
          </el-option>
        </el-select>
        
        <el-button
          type="primary"
          icon="el-icon-refresh"
          size="medium"
          @click="clearFilters">
          Reset
        </el-button>
      </div>
      
      <div class="project-summary">
        <span class="project-count">{{ filteredProjects.length }} projects available for download</span>
        <el-pagination
          @current-change="changePage"
          :current-page="currentPage"
          :page-size="itemsPerPage"
          layout="prev, pager, next"
          :total="filteredProjects.length">
        </el-pagination>
      </div>
    </div>

    <!-- Content Section with Project List -->
    <div class="content-section">
      <div class="project-list">
        <div
          v-for="project in paginatedProjects"
          :key="project.datasetid"
          class="modern-card project-card">
          <div class="project-card-content">
            <div class="project-header">
              <div class="project-title-section">
                <h3 class="project-title">
                  {{ project.name }}
                  <span class="dataset-id">ID: No_{{ String(project.datasetid).replace(/^No_?/i, '').padStart(6, '0') }}</span>
                </h3>
                <div class="project-tags">
                  <el-tag v-if="project.datasetType === 'private'" type="warning" size="small" class="private-tag">
                    <i class="el-icon-lock"></i> Private Dataset
                  </el-tag>
                  <el-tag v-else type="success" size="small" class="public-tag">
                    <i class="el-icon-view"></i> Public Dataset
                  </el-tag>
                  <el-tag type="primary" size="small">{{ project.cancerType }}</el-tag>
                  <el-tag type="info" size="small">{{ project.tissueType }}</el-tag>
                </div>
              </div>
              <div v-if="project.pubmedId" class="project-meta">
                <div class="pubmed-link">
                  <el-button
                    type="text"
                    size="small"
                    icon="el-icon-link"
                    @click.stop="openPubMed(project.pubmedId)">
                    PubMed: {{ project.pubmedId }}
                  </el-button>
                </div>
              </div>
            </div>
            
            <div class="project-description">
              <p :class="{ expanded: expandedDescriptions[project.datasetid] }">
                {{ project.description }}
              </p>
              <span 
                v-if="isTextTruncated(project.description) && !expandedDescriptions[project.datasetid]" 
                class="load-more-indicator"
                @click="toggleDescription(project.datasetid)"
              >
                ... <span class="load-more-text">load more</span>
              </span>
              <span 
                v-if="expandedDescriptions[project.datasetid]" 
                class="load-more-indicator"
                @click="toggleDescription(project.datasetid)"
              >
                <span class="load-more-text">show less</span>
              </span>
            </div>
            
            <div class="project-stats">
              <div class="stat-item">
                <i class="el-icon-s-data stat-icon"></i>
                <span class="stat-label">{{ project.samples }} Samples</span>
              </div>
            </div>
          </div>
          
          <div class="project-card-actions">
            <el-tooltip
              :content="project.datasetType === 'private' ? 'Private datasets cannot be downloaded' : 'Download dataset'"
              placement="top">
              <el-button
                type="primary"
                size="small"
                icon="el-icon-download"
                :loading="downloadingIds.includes(project.datasetid)"
                :disabled="project.datasetType === 'private'"
                @click.stop="downloadProject(project)">
                Download
              </el-button>
            </el-tooltip>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="filteredProjects.length === 0" class="modern-card empty-state">
        <div class="empty-content">
          <i class="el-icon-search empty-icon"></i>
          <h3>No projects found</h3>
          <p>Try adjusting your search criteria or filters</p>
          <el-button type="primary" @click="clearFilters">Clear all filters</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'DownloadView',
  props: {
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    currentUser: {
      type: Object,
      default: () => ({ username: '', email: '', role: 'community' })
    }
  },
  data() {
    return {
      searchQuery: '',
      selectedCancerType: '',
      selectedTissueType: '',
      selectedDatasetType: '',
      projects: [],
      filteredProjects: [],
      currentPage: 1,
      itemsPerPage: 5,
      downloadingIds: [],
      expandedDescriptions: {}
    }
  },
  computed: {
    cancerTypes() {
      return [...new Set(this.projects.map(p => p.cancerType))].sort()
    },
    tissueTypes() {
      return [...new Set(this.projects.map(p => p.tissueType))].sort()
    },
    paginatedProjects() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredProjects.slice(start, end);
    }
  },
  mounted() {
    this.fetchProjects()
  },
  methods: {
    extractDatasetNumber(datasetid) {
      const match = datasetid.match(/^No_(\d+)$/);
      return match ? parseInt(match[1], 10) : 0;
    },
    
    async fetchProjects() {
      try {
        const resp = await axios.post('/api/projectSearch/list_projects')
        if (resp && resp.data && resp.data.code === 200) {
          const rows = Array.isArray(resp.data.data) ? resp.data.data : []
          const normalized = rows.map(r => ({
              datasetid: r.datasetid || r.datasetId || '',
              name: r.name || '',
              description: r.description || '',
              pubmedId: r.pubmedId ?? null,
              samples: r.samples ?? 0,
              cancerType: r.cancerType || '',
              tissueType: r.tissueType || '',
              datasetType: r.datasetType || 'public'
            }))
          
          // Filter out private datasets for users who don't have access
          let filtered = normalized;
          if (!this.isLoggedIn || this.currentUser.role === 'community') {
            filtered = normalized.filter(project => project.datasetType !== 'private');
          }

          filtered.sort((a, b) => {
            const numA = this.extractDatasetNumber(a.datasetid);
            const numB = this.extractDatasetNumber(b.datasetid);
            return numA - numB;
          });

          this.projects = filtered
          this.filteredProjects = [...this.projects]
          this.currentPage = 1
        } else {
          this.$notify && this.$notify({
            title: 'Error',
            message: 'Failed to load projects',
            type: 'error',
            position: 'top-right'
          })
        }
      } catch (e) {
        this.$notify && this.$notify({
          title: 'Error',
          message: 'Error loading projects',
          type: 'error',
          position: 'top-right'
        })
      }
    },
    handleSearch() {
      this.applyFilters()
    },
    applyFilters() {
      let filtered = [...this.projects]
      
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(project =>
          project.name.toLowerCase().includes(query) ||
          project.description.toLowerCase().includes(query) ||
          project.cancerType.toLowerCase().includes(query) ||
          project.tissueType.toLowerCase().includes(query) ||
          project.datasetid.toLowerCase().includes(query) ||
          (project.pubmedId && project.pubmedId.toString().toLowerCase().includes(query))
        )
      }
      
      if (this.selectedCancerType) {
        filtered = filtered.filter(project => project.cancerType === this.selectedCancerType)
      }
      
      if (this.selectedTissueType) {
        filtered = filtered.filter(project => project.tissueType === this.selectedTissueType)
      }
      
      if (this.selectedDatasetType) {
        if (this.selectedDatasetType === 'private') {
          // Only apply private filter if user has access
          if (this.isLoggedIn && this.currentUser.role !== 'community') {
            filtered = filtered.filter(project => project.datasetType === 'private')
          } else {
            // Reset filter for community users
            this.selectedDatasetType = ''
          }
        } else if (this.selectedDatasetType === 'public') {
          filtered = filtered.filter(project => project.datasetType === 'public')
        }
      }
      
      this.filteredProjects = filtered
    },
    clearFilters() {
      this.searchQuery = ''
      this.selectedCancerType = ''
      this.selectedTissueType = ''
      this.selectedDatasetType = ''
      this.filteredProjects = [...this.projects]
    },
    openPubMed(pubmedId) {
      window.open(`https://pubmed.ncbi.nlm.nih.gov/${pubmedId}/`, '_blank')
    },
    changePage(page) {
      this.currentPage = page;
    },
    isTextTruncated(text) {
      // Rough estimate: check if text is long enough to be truncated
      // Assuming average character width and 3 lines with about 100 chars per line
      return text && text.length > 300;
    },
    toggleDescription(datasetid) {
      this.$set(this.expandedDescriptions, datasetid, !this.expandedDescriptions[datasetid]);
    },
    async downloadProject(project) {
      this.downloadingIds.push(project.datasetid);
      
      try {
        const response = await axios.post('/api/projectSearch/download_dataset', {
          datasetid: project.datasetid
        }, {
          responseType: 'blob'
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${project.datasetid}_${project.name.replace(/\s+/g, '_')}.zip`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        this.$notify({
          title: 'Success',
          message: `Download started for ${project.name}`,
          type: 'success',
          position: 'top-right'
        });
      } catch (error) {
        this.$notify({
          title: 'Error',
          message: 'Failed to download dataset: ' + (error.response?.data?.error || error.message),
          type: 'error',
          position: 'top-right'
        });
      } finally {
        this.downloadingIds = this.downloadingIds.filter(id => id !== project.datasetid);
      }
    }
  }
}
</script>

<style scoped>
/* App Container - Full Height Layout */
.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* Navigate Bar - Search Filter and Project Summary Header */
.navigate-bar {
  display: flex;
  flex-direction: column;
  padding: 20px 3vw 10px 3vw;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.95);
  transition: all 0.3s ease;
  gap: 12px;
}

/* Search Filter Section */
.search-filter-section {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
}

.search-input {
  flex: 1;
  min-width: 200px;
}

.filter-select {
  min-width: 140px;
}

/* Project Summary */
.project-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-top: 4px;
}

.project-count {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

/* Content Section - Scrollable with Responsive Padding */
.content-section {
  flex: 1;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  padding-top: clamp(15px, 3vw, 21px);
  padding-bottom: clamp(15px, 3vw, 30px);
  padding-left: max(clamp(15px, 3vw, 30px), calc((100vw - 1600px) / 2 + clamp(15px, 3vw, 30px)));
  padding-right: max(clamp(15px, 3vw, 30px), calc((100vw - 1600px) / 2 + clamp(15px, 3vw, 30px)));
}

/* Project List */
.project-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.modern-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.project-card {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  padding: 20px 24px;
  min-height: 180px;
}

.project-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.private-tag {
  font-weight: 600;
  background-color: #fef3c7 !important;
  border-color: #f59e0b !important;
  color: #92400e !important;
}

/* Public Dataset Tag Styling */
.public-tag {
  font-weight: 600;
  background-color: #d1fae5 !important;
  border-color: #67C23A !important;
  color: #166534 !important;
}

.project-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0; /* Allow flex children to shrink */
}

.project-card-content > * {
  flex-shrink: 0; /* Prevent header and stats from shrinking */
}

.project-card-content .project-description {
  flex-shrink: 1; /* Allow description to shrink if needed */
}

.project-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
}

.project-title-section {
  flex: 1;
}

.project-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 10px 0;
  line-height: 1.3;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.dataset-id {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  background: #f8fafc;
  padding: 2px 8px;
  border-radius: 4px;
  letter-spacing: 0.3px;
  font-family: 'Monaco', 'Courier New', monospace;
}

.project-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.project-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.pubmed-link .el-button {
  padding: 4px 8px;
  font-size: 12px;
  color: #3b82f6;
}

.project-description {
  color: #6b7280;
  line-height: 1.5;
  font-size: 14px;
  flex: 1;
  overflow: visible;
  position: relative;
}

.project-description p {
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 4.5em; /* 3 lines * 1.5 line-height */
  transition: max-height 0.3s ease;
}

.project-description p.expanded {
  display: block;
  -webkit-line-clamp: unset;
  line-clamp: unset;
  max-height: none;
  overflow: visible;
}

.load-more-indicator {
  display: inline-block;
  margin-top: 4px;
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  cursor: pointer;
  user-select: none;
}

.load-more-indicator:hover {
  color: #6b7280;
}

.load-more-text {
  color: #3b82f6;
  font-weight: 500;
  font-style: normal;
}

.load-more-text:hover {
  color: #2563eb;
  text-decoration: underline;
}

.project-stats {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 13px;
}

.stat-icon {
  font-size: 14px;
  color: #9ca3af;
}

.project-card-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-left: 20px;
  justify-content: center;
  align-self: center;
}

.project-card-actions .el-button {
  min-width: 120px;
}

/* Empty State */
.empty-state {
  padding: 48px 20px;
}

.empty-content {
  text-align: center;
  color: #6b7280;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.empty-content h3 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 6px 0;
  color: #374151;
}

.empty-content p {
  margin: 0 0 16px 0;
  font-size: 14px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .search-filter-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-input {
    width: 100%;
  }
  
  .filter-select {
    width: 100%;
  }
  
  .project-summary {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
  
  .project-header {
    flex-direction: column;
    gap: 12px;
  }
  
  .project-card {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
  
  .project-card-actions {
    flex-direction: row;
    margin-left: 0;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .navigate-bar {
    padding: 12px 15px;
  }
  
  .project-stats {
    flex-direction: column;
    gap: 8px;
  }
  
  .project-card-actions {
    flex-direction: column;
  }
}
</style>
